import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { debounce } from "lodash";
// @mui
import { Container, Typography, Stack, Chip, Switch, FormControlLabel, TextField, Button, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
// components
import { useSettingsContext } from '../../components/settings';
import CustomPagination from '../../components/custom-pagination';
import Iconify from '../../components/iconify';
import CustomModal from "../../components/custom-modal";
import ComorbiditiesView from './ComorbiditiesView';
import CustomGrid from '../../components/custom-grid';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { searchComorbidities,onPortalUpdateComorbidityStatus } from '../../APIs/comorbidities';
import {getComorbiditiesListColumns} from './ComorbiditiesColumns'

// ----------------------------------------------------------------------
import { useAuthContext } from '../../auth/useAuthContext';

export default function ComorbiditiesList() {
    const { themeStretch } = useSettingsContext();
    const axiosPrivate = useAxiosPrivate();
    const { user } = useAuthContext();
    const [includeOnlyActive, setIncludeOnlyActive] = useState(false);
    const [totalComorbidities, setTotalComorbidities] = useState(0);
    const [comorbidities, setComorbidities] = useState([]);    
    const [term, setTerm] = useState();
    const [fullWidth] = useState(true);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [openModal, setOpenModal] = useState(false);
    const [selectedComorbidity, setSelectedComorbidity] = useState();
    const [readOnly, setReadOnly] = useState(true);
    
    const getComorbidities = (currentPage = 1) => {
        const request = { includeOnlyActive,term, size, page: currentPage ?? page }
        setPage(request.page);

        searchComorbidities(axiosPrivate, request, (response) => {
            const { data, total } = response;
            setComorbidities(data);
            setTotalComorbidities(total);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getComorbidities, [includeOnlyActive,term, size]);    

    const handleChange = (event) => {
        setIncludeOnlyActive(event.target.checked);
    };

    const handleSearch = (event) => {
        setTerm(event.target.value);
    }

    const handlePageChange = (pageIndex) => {
        setPage(pageIndex);
        getComorbidities(pageIndex);
    }

    const handlePageSizeChange = (pageSize) => {
        setSize(pageSize);
    }

    const onHandleClose = () => {
        setSelectedComorbidity();
        setReadOnly(true);
        setOpenModal(false);
    }

    const handleRowClick = ({ row }) => {
        setSelectedComorbidity(row);
        setOpenModal(true);
        setReadOnly(true);
    }

    const onEdit = (id) => {
        const _comorbidities = comorbidities.find(c => c.id === id);
        setSelectedComorbidity(_comorbidities);
        setReadOnly(false);
        setOpenModal(true);
    }

    const onAdd = () => {
        setReadOnly(false);
        setOpenModal(true);
    }

    const handleComorbiditiesChange = () => {
        getComorbidities(page);
        onHandleClose();
    }

    const onDeactive = (id) => {
        onPortalUpdateComorbidityStatus(axiosPrivate, { id, isActive: false }, (response) => {
            if (response.status === 200) {
                getComorbidities(page);
            }
        });
    }

    const onActive = (id) => {
        onPortalUpdateComorbidityStatus(axiosPrivate, { id, isActive: true }, (response) => {
            if (response.status === 200) {
                getComorbidities(page);
            }
        });
    }

    const debouncedChangeHandler = debounce(handleSearch, 500);

    const columns = getComorbiditiesListColumns({ onEdit, onActive, onDeactive  });

    return (
        <>
            <Helmet>
                <title>Comorbidities | flok</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'xl'}>
                <Stack
                    flexGrow={1}
                    direction="column"
                    justifyContent="flex-start"
                    spacing={{ xs: 0.5, sm: 1.5 }}>
                    <Typography variant="h4" component="h4" paragraph>
                    Comorbidities <Chip label={totalComorbidities} />
                        <Button style={{ marginLeft: '2%', minWidth: 0 }} variant="outlined" onClick={onAdd}>+</Button>
                    </Typography>
                    <Stack
                        flexGrow={1}
                        direction="row"
                        justifyContent="flex-start"
                        spacing={{ xs: 0.5, sm: 1.5 }}>
                        <TextField
                            hiddenLabel
                            placeholder="Search..."
                            size="small"
                            onChange={debouncedChangeHandler}
                            fullWidth={fullWidth}
                            defaultValue={term}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeOnlyActive}
                                    onChange={handleChange}
                                    inputProps={{ 'aria-label': 'controlled' }} />
                            }
                            label="Hide Deleted Comorbidities" />
                    </Stack>
                    <CustomGrid>
                        <DataGrid
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'lightgray'
                                },
                                '.MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '&.MuiDataGrid-root': {
                                    border: 'none'
                                },
                                '.MuiDataGrid-row': {
                                    cursor: 'pointer'
                                },
                                '.MuiDataGrid-footerContainer': {
                                    display: 'none'
                                }
                            }}
                            rows={comorbidities}
                            columns={columns}
                            disableColumnMenu
                            disableColumnFilter
                            disableRowSelectionOnClick
                            hideFooterPagination
                            onRowClick={handleRowClick}
                        />
                    </CustomGrid>
                    <CustomPagination pageSize={size} currentPage={page} total={totalComorbidities} handleChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} />
                    <CustomModal open={openModal} onClose={onHandleClose}>
                        <ComorbiditiesView onChange={handleComorbiditiesChange} item={selectedComorbidity} onHandleClose={onHandleClose} readOnly={readOnly} />
                    </CustomModal>
                </Stack>
            </Container>
        </>
    );
}

